'use client';

/**
 * Pricing Modal 状态管理
 * 全局管理定价弹窗的打开/关闭状态
 */
import { create } from 'zustand';
import { trackFunnelEvent } from '@/lib/marketing/funnel';
import type { MembershipRequirement } from '@/lib/membership-requirement';
import type { DiscountCouponClaim } from '@/types/discount-coupons';
import type { SubscriptionPlanSurface } from '@/lib/api/subscriptions';

export type PricingModalTrigger = 'quota_exceeded' | 'feature_locked' | 'manual' | 'upgrade';
export type PricingModalSource =
  | 'credits_exhausted'
  | 'soul_id_creation'
  | 'template_trial'
  | 'template_membership_use'
  | 'project_limit'
  | 'project_item_limit';

interface PricingModalState {
  isOpen: boolean;
  triggerReason: PricingModalTrigger;
  quotaType?: string;
  featureLabel?: string;
  membershipRequirement?: MembershipRequirement;
  requiredCredits?: number;
  availableCredits?: number;
  currentTier?: string;
  defaultTab?: 'upgrade' | 'topup';
  source?: PricingModalSource;
  planSurface?: SubscriptionPlanSurface;
  preferredSubscriptionPlanSlug?: string;
  preferredSubscriptionPriceVariantId?: string;
  eligibleSubscriptionPlanSlugs?: string[];
  preferredTopupPackSlug?: string;
  auditMode?: boolean;
  couponClaim?: DiscountCouponClaim | null;
  onSuccess?: () => void;
}

interface PricingModalStore extends PricingModalState {
  openPricingModal: (options?: {
    triggerReason?: PricingModalTrigger;
    quotaType?: string;
    featureLabel?: string;
    membershipRequirement?: MembershipRequirement;
    requiredCredits?: number;
    availableCredits?: number;
    currentTier?: string;
    defaultTab?: 'upgrade' | 'topup';
    source?: PricingModalSource;
    planSurface?: SubscriptionPlanSurface;
    preferredSubscriptionPlanSlug?: string;
    preferredSubscriptionPriceVariantId?: string;
    eligibleSubscriptionPlanSlugs?: string[];
    preferredTopupPackSlug?: string;
    auditMode?: boolean;
    couponClaim?: DiscountCouponClaim | null;
    onSuccess?: () => void;
  }) => void;
  closePricingModal: () => void;
}

export const usePricingModalStore = create<PricingModalStore>((set, get) => ({
  isOpen: false,
  triggerReason: 'manual',
  quotaType: undefined,
  requiredCredits: undefined,
  availableCredits: undefined,
  currentTier: undefined,
  defaultTab: undefined,
  source: undefined,
  planSurface: 'web',
  preferredSubscriptionPlanSlug: undefined,
  preferredSubscriptionPriceVariantId: undefined,
  eligibleSubscriptionPlanSlugs: undefined,
  preferredTopupPackSlug: undefined,
  auditMode: false,
  couponClaim: null,
  onSuccess: undefined,

  openPricingModal: (options) => {
    const wasOpen = get().isOpen;
    // A single user action can reject at more than one boundary. Preserve the
    // first context instead of replacing an open checkout with another lock.
    if (get().isOpen && (options?.triggerReason === 'feature_locked' || options?.triggerReason === 'quota_exceeded')) return;
    if (!wasOpen) trackFunnelEvent('pricing_opened');
    set({
      isOpen: true,
      triggerReason: options?.triggerReason || 'manual',
      quotaType: options?.quotaType,
      featureLabel: options?.featureLabel,
      membershipRequirement: options?.membershipRequirement,
      requiredCredits: options?.requiredCredits,
      availableCredits: options?.availableCredits,
      currentTier: options?.currentTier,
      // All purchase entries start with plans. Add credits switches to packs
      // inside the open modal; legacy top-up links must not skip subscriptions.
      defaultTab: 'upgrade',
      source: options?.source,
      planSurface: options?.planSurface ?? 'web',
      preferredSubscriptionPlanSlug: options?.preferredSubscriptionPlanSlug,
      preferredSubscriptionPriceVariantId: options?.preferredSubscriptionPriceVariantId,
      eligibleSubscriptionPlanSlugs: options?.eligibleSubscriptionPlanSlugs,
      preferredTopupPackSlug: options?.preferredTopupPackSlug,
      auditMode: options?.auditMode ?? false,
      couponClaim: options?.couponClaim ?? null,
      onSuccess: options?.onSuccess,
    });
  },

  closePricingModal: () => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('membership-entitlements-changed'));
    set({
      isOpen: false,
      triggerReason: 'manual',
      quotaType: undefined,
      featureLabel: undefined,
      membershipRequirement: undefined,
      requiredCredits: undefined,
      availableCredits: undefined,
      currentTier: undefined,
      defaultTab: undefined,
      source: undefined,
      planSurface: 'web',
      preferredSubscriptionPlanSlug: undefined,
      preferredSubscriptionPriceVariantId: undefined,
      eligibleSubscriptionPlanSlugs: undefined,
      preferredTopupPackSlug: undefined,
      auditMode: false,
      couponClaim: null,
      onSuccess: undefined,
    });
  },
}));

// 便捷方法，可以直接导入使用
export const pricingModal = {
  open: (options?: Parameters<PricingModalStore['openPricingModal']>[0]) => {
    usePricingModalStore.getState().openPricingModal(options);
  },
  close: () => {
    usePricingModalStore.getState().closePricingModal();
  },
};
